import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import { Row, Col } from "antd"
import { FormField } from "../../../components/Form/FormField"
import { useContainerInjection } from "../../../hooks/useContainerInjection"
import { SellerServicesViewModel } from "../SellerServicesViewModel"
import { SellerServiceDetail } from "../../../../domain/entities/SellerServiceDetail"
import Switch from "../../../components/Switch/Switch"
import { AuthStore } from "../../../stores/AuthStore"
import { Typography, message } from "antd"
import dayjs from "dayjs"
import { InputType } from "../../../components/TextInput/const/InputType.enum"

interface Props {
	data: Partial<SellerServiceDetail>
	disabled?: boolean
	onChange?: (value: any) => void
}

const TournamentSectionForm = ({ data, disabled, onChange }: Props) => {
	const { t } = useTranslation("services", { keyPrefix: "form.eventInfo" })
	const { t: tValidations } = useTranslation("services", { keyPrefix: "form.validations" })
	const { isAdmin } = useContainerInjection<AuthStore>("AuthStore")
	const [messageApi, contextHolder] = message.useMessage()

	return (
		<>
			{data?.isTournament && (
				<>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline",
							marginTop: "1rem"
						}}
					>
						<Typography.Text>{t("eventDateTime")} :</Typography.Text>
						<FormField.DatePicker
							key={"eventDateTime"}
							value={data?.eventDateTime ? dayjs(data?.eventDateTime) : undefined}
							mandatory={true}
							disabled={disabled || !isAdmin}
							showTime={true}
							onChange={eventDate =>
								onChange &&
								onChange({
									eventDateTime: eventDate?.toDate()
								})
							}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("prizes")} :</Typography.Text>
						<FormField.Input
							key={"prizes"}
							value={data?.prizes}
							mandatory={true}
							name={"prizes"}
							onChange={prizes => onChange && onChange({ prizes })}
							disabled={disabled || !isAdmin}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("place")} :</Typography.Text>
						<FormField.Input
							key={"place"}
							value={data?.place}
							mandatory={true}
							name={"place"}
							onChange={place => onChange && onChange({ place })}
							disabled={disabled || !isAdmin}
						/>
					</div>

					{/* <div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("estimatedDuration")} :</Typography.Text>
						<FormField.Input
							key={"estimatedDuration"}
							value={data?.estimatedDuration}
							inputType={InputType.NUMBER}
							mandatory={true}
							name={"estimatedDuration"}
							onChange={duration => onChange && onChange({ estimatedDuration: Number(duration) })}
							disabled={disabled || !isAdmin}
						/>
					</div> */}

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("finishDate")} :</Typography.Text>
						<FormField.DatePicker
							key={"finishDate"}
							defaultValue={dayjs(new Date())}
							value={dayjs(data?.finishTournamentDate ?? new Date())}
							mandatory={true}
							disabled={disabled || !isAdmin}
							onChange={finishDate =>
								onChange &&
								onChange({
									finishTournamentDate: finishDate?.toDate()
								})
							}
						/>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("availablePlaces")} :</Typography.Text>
						<FormField.Input
							key={"availableTournamentPlaces"}
							style={{ width: "25%" }}
							value={data?.availableTournamentPlaces}
							inputType={InputType.NUMBER}
							mandatory={true}
							name={"availableTournamentPlaces"}
							onChange={availablePlaces =>
								onChange && onChange({ availableTournamentPlaces: Number(availablePlaces) })
							}
							disabled={disabled || !isAdmin}
						/>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("participants")} :</Typography.Text>
						<FormField.Input
							key={"participants"}
							style={{ width: "25%" }}
							value={data?.participants}
							inputType={InputType.NUMBER}
							mandatory={true}
							name={"participants"}
							onChange={participants => onChange && onChange({ participants: Number(participants) })}
							disabled={disabled || !isAdmin}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("googleMapsLocation")} :</Typography.Text>
						<FormField.Input
							key={"googleMapsUrl"}
							value={data?.googleMapsUrl}
							name={"googleMapsUrl"}
							onChange={url => onChange && onChange({ googleMapsUrl: url })}
							disabled={disabled || !isAdmin}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("eventType")} :</Typography.Text>
						<FormField.Select
							key={"eventType"}
							value={data?.eventType}
							style={{ width: "200px" }}
							options={[
								{ value: "tournament", label: t("eventTypes.tournament") },
								{ value: "race", label: t("eventTypes.race") },
								{ value: "event", label: t("eventTypes.event") }
							]}
							mandatory={true}
							onChange={type => onChange && onChange({ eventType: type })}
							disabled={disabled || !isAdmin}
						/>
					</div>

					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline"
						}}
					>
						<Typography.Text>{t("endDateTime")} :</Typography.Text>
						<>
							{contextHolder}
							<FormField.DatePicker
								key={"endDateTime"}
								value={data?.endDateTime ? dayjs(data?.endDateTime) : undefined}
								disabled={disabled || !isAdmin}
								showTime={true}
								allowClear={true}
								onChange={endDate => {
									if (!endDate) {
										onChange &&
											onChange({
												endDateTime: null
											})
										return
									}

									if (data?.eventDateTime && endDate.toDate() <= new Date(data.eventDateTime)) {
										messageApi.error({
											content: tValidations("endDateTimeAfter"),
											duration: 3
										})
										return
									}
									onChange &&
										onChange({
											endDateTime: endDate.toDate()
										})
								}}
							/>
						</>
					</div>
				</>
			)}
		</>
	)
}

export default observer(TournamentSectionForm)
